// src/pages/Checkout.jsx
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const paypalRef = useRef(null);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [error, setError] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE;

  // bookingId from state or query
  const bookingId =
    location.state?.bookingId ||
    new URLSearchParams(location.search).get("bookingId");

  useEffect(() => {
    if (!bookingId) {
      setError("Missing booking reference");
      return;
    }
    // fetch booking summary
    axios
      .get(`${API_BASE}/bookings/${bookingId}`)
      .then((res) => {
        if (res.data.success) setBooking(res.data.booking);
        else setError("Failed to load booking");
      })
      .catch((e) => {
        console.error(e);
        setError("Failed to load booking");
      });
  }, [bookingId, API_BASE]);

  // Load PayPal SDK dynamically when selecting PayPal
  useEffect(() => {
    if (selectedGateway === "paypal" && booking) {
      // insert PayPal script if not present
      if (!window.paypal) {
        const script = document.createElement("script");
        script.src = `https://www.paypal.com/sdk/js?client-id=${process.env.REACT_APP_PAYPAL_CLIENT_ID}&currency=${
          booking.currency || "USD"
        }`;
        script.async = true;
        script.onload = () => renderPayPalButtons();
        document.body.appendChild(script);
      } else {
        renderPayPalButtons();
      }
    }
    // eslint-disable-next-line
  }, [selectedGateway, booking]);

  // Render PayPal Buttons
  const renderPayPalButtons = () => {
    if (!window.paypal || !paypalRef.current) return;
    paypalRef.current.innerHTML = "";

    window.paypal.Buttons({
      style: {
        layout: "vertical",
        color: "gold",
        shape: "rect",
        label: "paypal",
      },
      createOrder: async () => {
        // create order on server using booking.total
        const amount = booking.total.toFixed(2);
        const res = await axios.post(`${API_BASE}/payment/paypal`, {
          amount,
          currency: booking.currency || "USD",
          description: `Booking ${booking._id}`,
        });
        return res.data.paypal_order_id;
      },
      onApprove: async (data) => {
        setLoading(true);
        try {
          // Capture on server with bookingId so server updates booking + sends invoice
          const verifyRes = await axios.post(`${API_BASE}/payment/paypal/verify`, {
            paypal_order_id: data.orderID,
            bookingId: booking._id,
          });

          if (verifyRes.data.success) {
            alert("✅ Payment successful — booking confirmed!");
            navigate("/success", { state: { bookingId: booking._id } });
          } else {
            alert("Payment verification failed");
          }
        } catch (err) {
          console.error(err);
          alert("Payment capture failed");
        } finally {
          setLoading(false);
        }
      },
      onError: (err) => {
        console.error("PayPal error", err);
        alert("PayPal error. Try again.");
      },
    }).render(paypalRef.current);
  };

  // Razorpay flow
  const handleRazorpay = async () => {
    if (!booking) return;
    setLoading(true);
    try {
      const amountInINR = booking.amountInINR || booking.total; // adjust as needed
      const { data } = await axios.post(`${API_BASE}/payment/razorpay`, {
        amountInINR,
        notes: { bookingId: booking._id },
      });

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: data.amount, // in paise
        currency: data.currency,
        name: "ARO Events",
        description: `Booking ${booking._id}`,
        order_id: data.id,
        handler: async function (response) {
          try {
            const verifyRes = await axios.post(`${API_BASE}/payment/verify`, {
              razorpay_order_id: data.id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              bookingId: booking._id,
              amount: data.amount,
            });

            if (verifyRes.data.success) {
              alert("✅ Razorpay payment successful!");
              navigate("/success", { state: { bookingId: booking._id } });
            } else {
              alert("Payment verification failed");
            }
          } catch (err) {
            console.error(err);
            alert("Verification failed");
          }
        },
        prefill: {
          name:
            booking.delegates[0]?.firstName +
            " " +
            booking.delegates[0]?.lastName,
          email: booking.delegates[0]?.email,
          contact: booking.delegates[0]?.mobile,
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error(err);
      alert("Razorpay order creation failed");
    } finally {
      setLoading(false);
    }
  };

  if (error)
    return (
      <div className="container py-5">
        <h3>{error}</h3>
      </div>
    );
  if (!booking)
    return (
      <div className="container py-5">
        <h3>Loading booking...</h3>
      </div>
    );

  return (
    <div className="container py-5" style={{ maxWidth: 900 }}>
      <h2>Checkout</h2>
      <p>
        Booking ID: <strong>{booking._id}</strong>
      </p>

      <div className="card p-3 mb-3">
        <h5>Order Summary</h5>
        <p>Delegates: {booking.delegates.length}</p>
        <p>Subtotal: US${(booking.subtotal || 0).toFixed(2)}</p>
        <p>Tax: US${(booking.tax || 0).toFixed(2)}</p>
        <h4>
          Total:{" "}
          <span className="text-primary">US${(booking.total || 0).toFixed(2)}</span>
        </h4>
      </div>

      <div className="card p-3 mb-3">
        <h5>Select Payment Method</h5>
        <div style={{ display: "flex", gap: 12 }}>
          <button
            onClick={() => setSelectedGateway("paypal")}
            style={{
              padding: 12,
              border:
                selectedGateway === "paypal" ? "2px solid #003087" : "1px solid #ccc",
              background: selectedGateway === "paypal" ? "#003087" : "#fff",
              color: selectedGateway === "paypal" ? "#fff" : "#003087",
            }}
          >
            PayPal
          </button>

          <button
            onClick={() => {
              setSelectedGateway("razorpay");
              handleRazorpay();
            }}
            disabled={loading}
            style={{
              padding: 12,
              border:
                selectedGateway === "razorpay"
                  ? "2px solid #3399cc"
                  : "1px solid #ccc",
              background: selectedGateway === "razorpay" ? "#3399cc" : "#fff",
              color: selectedGateway === "razorpay" ? "#fff" : "#3399cc",
            }}
          >
            {loading ? "Processing..." : "Razorpay"}
          </button>
        </div>

        {/* PayPal render area */}
        {selectedGateway === "paypal" && (
          <div ref={paypalRef} style={{ marginTop: 20 }}></div>
        )}
      </div>
    </div>
  );
}
